/**
 * <faq-tabs-nav>
 *
 * JS-built tab nav for the FAQ page's tabbed layout (sections/faq.liquid,
 * layout "header-tabs_faqs"). A parent Liquid scope cannot read nested theme
 * block settings (content_for 'blocks' is opaque, block objects expose no
 * .blocks accessor), so the panels rendered by blocks/faq-tab.liquid are the
 * single source of truth: each panel carries `data-tab-label` (button text)
 * and `id` (target for aria-controls). Renaming either attribute in
 * faq-tab.liquid breaks this component.
 *
 * No fetch, no re-render: all panels are server-rendered once by Liquid and
 * left visible (no-JS fallback = stacked panels); this component only toggles
 * `hidden` on them and manages a roving-tabindex tablist.
 */
class FaqTabsNav extends HTMLElement {
  #controller = null;
  #panels = [];
  #buttons = [];

  connectedCallback() {
    this.#build();

    this.#controller = new AbortController();
    document.addEventListener('shopify:block:select', (event) => this.#onBlockSelect(event), {
      signal: this.#controller.signal,
    });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }

  #build() {
    // Idempotent: theme-editor section reloads re-run connectedCallback on the
    // same custom element instance, so clear any previously built buttons first.
    this.replaceChildren();
    this.#buttons = [];

    const section = this.closest('.faq');
    this.#panels = section ? Array.from(section.querySelectorAll('.faq-tab[data-tab-label]')) : [];

    if (this.#panels.length < 2) {
      return;
    }

    this.setAttribute('role', 'tablist');

    const fragment = document.createDocumentFragment();

    this.#panels.forEach((panel, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.setAttribute('role', 'tab');
      button.id = `faq-tab-btn-${panel.id}`;
      button.setAttribute('aria-controls', panel.id);
      button.textContent = panel.dataset.tabLabel;

      panel.setAttribute('aria-labelledby', button.id);

      button.addEventListener(
        'click',
        () => {
          this.#activate(index);
          button.focus();
        },
        { signal: this.#controller?.signal },
      );

      this.#buttons.push(button);
      fragment.appendChild(button);
    });

    this.addEventListener('keydown', (event) => this.#onKeydown(event), {
      signal: this.#controller?.signal,
    });

    this.appendChild(fragment);
    this.#activate(0);

    const container = this.#panels[0]?.parentElement;
    container?.classList.add('faq-tabs--ready');
  }

  #activate(index) {
    this.#panels.forEach((panel, i) => {
      panel.hidden = i !== index;
    });

    this.#buttons.forEach((button, i) => {
      const isActive = i === index;
      button.setAttribute('aria-selected', String(isActive));
      button.tabIndex = isActive ? 0 : -1;
    });

    // 'nearest' no-ops when the button is already fully visible (initial load,
    // wrapped desktop layout) and avoids scrolling the page vertically.
    this.#buttons[index]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  #onKeydown(event) {
    const currentIndex = this.#buttons.findIndex((button) => button === document.activeElement);
    if (currentIndex === -1) return;

    let nextIndex = null;

    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % this.#buttons.length;
        break;
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + this.#buttons.length) % this.#buttons.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = this.#buttons.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#activate(nextIndex);
    this.#buttons[nextIndex].focus();
  }

  #onBlockSelect(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const panelIndex = this.#panels.findIndex((panel) => panel === target || panel.contains(target));
    if (panelIndex === -1) return;

    this.#activate(panelIndex);
  }
}

if (!customElements.get('faq-tabs-nav')) {
  customElements.define('faq-tabs-nav', FaqTabsNav);
}
